import React, { useContext } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Home from "./Home";
import BlogIndex from "./blog/BlogIndex";
import BlogPost from "./blog/BlogPost";
import PillarPage from "./blog/PillarPage";
import SiteLayout from "./components/SiteLayout";
import DataDeletion from "./DataDeletion";
import PrivacyPolicy from "./PrivacyPolicy";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SignupPage from "./pages/SignupPage";
import RenterSearchPage from "./pages/RenterSearchPage";
import PropertyDetailsPage from "./pages/PropertyDetailsPage";
import InboxPage from "./pages/InboxPage";
import AccountPage from "./pages/AccountPage";
import RenterAppLayout from "./components/RenterAppLayout";
import OwnerAppLayout from "./components/OwnerAppLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthContextWeb, AuthProviderWeb } from "./context/AuthContextWeb";
import OwnerDashboardPage from "./pages/owner/OwnerDashboardPage";
import OwnerListingsPage from "./pages/owner/OwnerListingsPage";
import AddPropertyPage from "./pages/owner/AddPropertyPage";
import EditPropertyPage from "./pages/owner/EditPropertyPage";
import "./styles.css";
import "./appFeatures.css";
import "./modernUI.css";

function AppRootRedirect() {
  const { user, isAuthenticated } = useContext(AuthContextWeb);
  if (!isAuthenticated) return <Navigate to="/app/login" replace />;
  const role = String(user?.role || "").toUpperCase();
  if (["OWNER", "ADMIN", "SUPER_ADMIN"].includes(role)) {
    return <Navigate to="/owner/dashboard" replace />;
  }
  return <Navigate to="/app/search" replace />;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProviderWeb>
        <Router>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/data-deletion" element={<DataDeletion />} />

            <Route path="/app/login" element={<LoginPage />} />
            <Route
              path="/app/forgot-password"
              element={<ForgotPasswordPage />}
            />
            <Route path="/app/signup" element={<SignupPage />} />

            {/* Blog routes with consistent layout */}
            <Route element={<SiteLayout />}>
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/pillar/:pillar" element={<PillarPage />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
            </Route>

            {/* Renter-only routes */}
            <Route element={<ProtectedRoute allowRoles={["USER", "RENTER"]} />}>
              <Route element={<RenterAppLayout />}>
                <Route path="/app/search" element={<RenterSearchPage />} />
                <Route
                  path="/app/properties/:id"
                  element={<PropertyDetailsPage />}
                />
                <Route path="/app/inbox" element={<InboxPage />} />
                <Route path="/app/account" element={<AccountPage />} />
              </Route>
            </Route>

            <Route path="/app" element={<AppRootRedirect />} />

            {/* ── Owner Portal ── */}
            <Route
              element={
                <ProtectedRoute
                  allowRoles={["OWNER", "ADMIN", "SUPER_ADMIN"]}
                />
              }
            >
              <Route element={<OwnerAppLayout />}>
                <Route
                  path="/owner/dashboard"
                  element={<OwnerDashboardPage />}
                />
                <Route path="/owner/listings" element={<OwnerListingsPage />} />
                <Route
                  path="/owner/add-property"
                  element={<AddPropertyPage />}
                />
                <Route
                  path="/owner/edit-property/:id"
                  element={<EditPropertyPage />}
                />
                <Route
                  path="/owner/properties/:id"
                  element={<PropertyDetailsPage />}
                />
                <Route path="/owner/inbox" element={<InboxPage />} />
                <Route path="/owner/account" element={<AccountPage />} />
              </Route>
            </Route>
            <Route
              path="/owner"
              element={<Navigate to="/owner/dashboard" replace />}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProviderWeb>
    </HelmetProvider>
  );
}

export default App;
